import { useCallback, useEffect, useRef, useState } from 'react';
import { get, subscribe } from './api/client';
import { getAllTickets } from './api/pages';
import type { ChangeEvent } from './api/types';

/** Reload on every live change that touches these lists (or any, when none given). */
export function useLive(fn: (e: ChangeEvent) => void, lists?: string[] | null) {
  const ref = useRef(fn);
  ref.current = fn;
  const key = lists?.join(',') ?? '*';
  useEffect(() => subscribe((e) => {
    if (key === '*' || !e.lists.length || e.lists.some((l) => key.split(',').includes(l))) ref.current(e);
  }), [key]);
}

/** GET a path and keep it fresh: { data, error, loading, reload, setData }. */
export function useGet<T>(path: string | null, o: { lists?: string[] | null; live?: boolean; pages?: boolean } = {}) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(!!path);
  const cur = useRef(path);
  cur.current = path;
  const reload = useCallback(async () => {
    if (!path) return;
    try {
      const j = o.pages ? ((await getAllTickets(path)) as T) : await get<T>(path);
      if (cur.current === path) {
        setData(j);
        setError(null);
      }
    } catch (e) {
      if (cur.current === path) setError(e as Error);
    } finally {
      if (cur.current === path) setLoading(false);
    }
  }, [path, o.pages]);
  useEffect(() => {
    setLoading(!!path);
    setData(null);
    void reload();
  }, [reload, path]);
  useLive(() => {
    if (o.live !== false) void reload();
  }, o.lists);
  return { data, error, loading, reload, setData };
}

/** A remembered choice (localStorage), the same in every component that reads it. */
export function usePref<T>(key: string, initial: T): [T, (v: T) => void] {
  const read = () => {
    try {
      const s = localStorage.getItem('deleg8.' + key);
      return s === null ? initial : (JSON.parse(s) as T);
    } catch {
      return initial;
    }
  };
  const [v, setV] = useState<T>(read);
  // A different key (another list's filter) reads its own value.
  useEffect(() => setV(read()), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const f = (e: Event) => (e as CustomEvent<string>).detail === key && setV(read());
    addEventListener('deleg8-pref', f);
    return () => removeEventListener('deleg8-pref', f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const set = (x: T) => {
    setV(x);
    try {
      localStorage.setItem('deleg8.' + key, JSON.stringify(x));
    } catch {
      /* not kept */
    }
    dispatchEvent(new CustomEvent('deleg8-pref', { detail: key }));
  };
  return [v, set];
}

export function useWide(q = '(min-width: 1024px)') {
  const [on, setOn] = useState(() => window.matchMedia?.(q).matches ?? false);
  useEffect(() => {
    const m = window.matchMedia?.(q);
    if (!m) return;
    const f = () => setOn(m.matches);
    m.addEventListener('change', f);
    return () => m.removeEventListener('change', f);
  }, [q]);
  return on;
}
